/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function ConsequenceTablePanel({ levels, setLevels, rows, setRows }) {
  const addLevel = () => {
    if (levels.length >= 8) return;
    const id = newId();
    const newLevel = { id, order: levels.length + 1, label: `Level ${levels.length + 1}` };
    setLevels((prev) => [...prev, newLevel]);
    setRows((prev) => prev.map((r) => ({ ...r, cells: [...r.cells, { levelId: id, text: "" }] })));
  };

  const removeLevel = (levelId) => {
    setLevels((prev) => prev.filter((l) => l.id !== levelId));
    setRows((prev) =>
      prev.map((r) => ({ ...r, cells: r.cells.filter((c) => c.levelId !== levelId) }))
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: newId(),
        order: prev.length + 1,
        label: "New consequence type",
        cells: levels.map((l) => ({ levelId: l.id, text: "" })),
      },
    ]);
  };

  const removeRow = (rowId) => setRows((prev) => prev.filter((r) => r.id !== rowId));

  const updateLevelLabel = (levelId, label) =>
    setLevels((prev) => prev.map((l) => (l.id === levelId ? { ...l, label } : l)));

  const updateRowLabel = (rowId, label) =>
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, label } : r)));

  const updateCell = (rowId, levelId, text) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const has = r.cells.some((c) => c.levelId === levelId);
        return {
          ...r,
          cells: has
            ? r.cells.map((c) => (c.levelId === levelId ? { ...c, text } : c))
            : [...r.cells, { levelId, text }],
        };
      })
    );

  const getCell = (row, levelId) => row.cells.find((c) => c.levelId === levelId)?.text || "";

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        sx={{
          tableLayout: "fixed",
          width: "100%",
          borderCollapse: "collapse",
          "& td, & th": { border: "1px solid #bdbdbd", verticalAlign: "top", padding: "10px 12px" },
        }}
      >
        <colgroup>
          <col style={{ width: 200 }} />
          {levels.map((l) => (
            <col key={l.id} style={{ width: `${Math.floor(660 / levels.length)}px` }} />
          ))}
          <col style={{ width: 48 }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={{ bgcolor: "#ede7f6", color: "#52009b", fontWeight: 700, fontSize: 13 }}>
              Consequence Type
            </TableCell>
            {levels.map((level, idx) => (
              <TableCell
                key={level.id}
                sx={{ bgcolor: "#ede7f6", color: "#52009b", py: 1.5, px: 1.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#52009b", opacity: 0.6, display: "block", mb: 0.5, fontSize: 11 }}
                >
                  {idx + 1}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TextField
                    value={level.label}
                    onChange={(e) => updateLevelLabel(level.id, e.target.value)}
                    variant="standard"
                    fullWidth
                    inputProps={{ style: { color: "#52009b", fontWeight: 700, fontSize: 13 } }}
                    sx={{
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "rgba(82,0,155,0.3)",
                      },
                      "& .MuiInput-underline:hover:before": { borderBottomColor: "#52009b" },
                      "& .MuiInput-underline:after": { borderBottomColor: "#52009b" },
                    }}
                  />
                  {levels.length > 2 && (
                    <Tooltip title="Remove level">
                      <IconButton
                        size="small"
                        onClick={() => removeLevel(level.id)}
                        sx={{ color: "#52009b", p: 0.25, flexShrink: 0 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            ))}
            <TableCell sx={{ bgcolor: "#ede7f6", textAlign: "center", p: "4px !important" }}>
              {levels.length < 8 && (
                <Tooltip title="Add level">
                  <IconButton size="small" onClick={addLevel} sx={{ color: "#52009b" }}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} sx={{ "&:nth-of-type(even)": { bgcolor: "#fafafa" } }}>
              <TableCell sx={{ bgcolor: "#f5f5f5", verticalAlign: "middle" }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <TextField
                    value={row.label}
                    onChange={(e) => updateRowLabel(row.id, e.target.value)}
                    variant="standard"
                    fullWidth
                    inputProps={{ style: { fontWeight: 700, fontSize: 13, color: "#37474f" } }}
                    sx={{ "& .MuiInput-underline:before": { borderBottomColor: "transparent" } }}
                  />
                  <Tooltip title="Remove row">
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.id)}
                      sx={{ color: "#b71c1c", p: 0.25, flexShrink: 0 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              {levels.map((level) => (
                <TableCell key={level.id} sx={{ p: "8px 12px !important" }}>
                  <TextField
                    value={getCell(row, level.id)}
                    onChange={(e) => updateCell(row.id, level.id, e.target.value)}
                    multiline
                    fullWidth
                    variant="standard"
                    minRows={3}
                    placeholder="Enter description…"
                    inputProps={{ style: { fontSize: 12 } }}
                    sx={{
                      "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
                      "& .MuiInput-underline:hover:before": { borderBottomColor: "#ccc" },
                    }}
                  />
                </TableCell>
              ))}
              <TableCell sx={{ bgcolor: "#eceff1" }} />
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={levels.length + 2} sx={{ bgcolor: "#fafafa", py: 0.5 }}>
              <MDButton
                variant="text"
                color="info"
                startIcon={<AddIcon />}
                onClick={addRow}
                size="small"
              >
                Add consequence type
              </MDButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        Maximum 8 levels.
      </Typography>
    </Box>
  );
}
